import React from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';
import { contactInfo } from '../data/restaurantData';

const ContactSection = () => {
  const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const getDayStyle = (dayIndex) => {
    const isToday = dayIndex === today;
    const isClosed = contactInfo.hours[days[dayIndex]] === 'Cerrado';
    
    if (isToday) {
      return 'text-white font-medium bg-red-600/20 px-2 py-1 rounded';
    } else if (isClosed) {
      return 'text-gray-500';
    } else {
      return '';
    }
  };
  
  const getTimeStyle = (dayIndex) => {
    const isToday = dayIndex === today;
    const isClosed = contactInfo.hours[days[dayIndex]] === 'Cerrado';
    
    if (isToday && !isClosed) {
      return 'text-white font-medium';
    } else if (isToday && isClosed) {
      return 'text-red-400 font-medium';
    } else if (isClosed) {
      return 'text-gray-500';
    } else {
      return 'text-red-400';
    }
  };

  return (
    <section id="contact" className="py-32 bg-black/80 relative">
      <div className="max-w-7xl mx-auto px-8">
        {/* Section Header */}
        <div className="text-center mb-24 animate-fade-in-up">
          <div className="mb-8">
            <MapPin className="mx-auto text-red-600 opacity-80" size={48} />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-thin tracking-[0.2em] md:tracking-[0.3em] text-white mb-8 leading-tight">
            VISÍTANOS
          </h2>
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-red-600 to-transparent mx-auto mb-12"></div>
          <p className="text-lg text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
            Ubicados en el corazón histórico de Altea la Vella
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 animate-fade-in-up" style={{animationDelay: '0.3s'}}>
          {/* Contact Info */}
          <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-red-900/30 p-12 luxury-shadow">
            <div className="mb-8">
              <h3 className="text-3xl font-thin tracking-[0.2em] text-white mb-4 leading-tight">
                INFORMACIÓN DE CONTACTO
              </h3>
              <div className="w-16 h-px bg-red-600 mb-8"></div>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-start gap-6 group">
                <div className="p-3 bg-red-600/20 border border-red-600/30 rounded-full group-hover:bg-red-600/30 transition-colors duration-300">
                  <MapPin className="text-red-400" size={24} />
                </div>
                <div>
                  <h4 className="font-light text-white mb-2 tracking-wide">DIRECCIÓN</h4>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {contactInfo.address.street}<br />
                    {contactInfo.address.city}<br />
                    {contactInfo.address.country}
                  </p>
                </div>
              </div>
              
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
              
              <div className="flex items-start gap-6 group">
                <div className="p-3 bg-red-600/20 border border-red-600/30 rounded-full group-hover:bg-red-600/30 transition-colors duration-300">
                  <Phone className="text-red-400" size={24} />
                </div>
                <div>
                  <h4 className="font-light text-white mb-2 tracking-wide">TELÉFONO</h4>
                  <p className="text-gray-300 font-light text-lg tracking-wider">{contactInfo.phone}</p>
                </div>
              </div>
              
              <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
              
              <div className="flex items-start gap-6 group">
                <div className="p-3 bg-red-600/20 border border-red-600/30 rounded-full group-hover:bg-red-600/30 transition-colors duration-300">
                  <Mail className="text-red-400" size={24} />
                </div>
                <div>
                  <h4 className="font-light text-white mb-2 tracking-wide">CORREO ELECTRÓNICO</h4>
                  <p className="text-gray-300 font-light tracking-wide">{contactInfo.email}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-700">
              <h4 className="font-light text-white mb-6 tracking-wide">HORARIOS DE SERVICIO</h4>
              <div className="space-y-3 text-sm font-light text-gray-300 tracking-wide">
                {dayNames.map((dayName, index) => (
                  <React.Fragment key={dayName}>
                    <div className="flex justify-between items-center">
                      <span className={getDayStyle(index)}>{dayName}</span>
                      <span className={getTimeStyle(index)}>{contactInfo.hours[days[index]]}</span>
                    </div>
                    {index < dayNames.length - 1 && <div className="w-full h-px bg-gray-700"></div>}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* Location Visual */}
          <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-red-900/30 p-12 luxury-shadow flex items-center justify-center">
            <div className="text-center">
              <div className="mb-8">
                <MapPin size={80} className="text-red-600 opacity-60 mx-auto mb-6" />
                <h4 className="text-2xl font-thin tracking-wider text-white mb-4">ALTEA, COSTA BLANCA</h4>
                <div className="w-16 h-px bg-red-600 mx-auto mb-6"></div>
              </div>
              
              <div className="space-y-4 text-gray-300 font-light">
                <p className="text-lg leading-relaxed">
                  En el encantador entorno de Altea la Vella, Restaurante Nou Valentino ofrece una experiencia 
                  gastronómica única en un ambiente acogedor, con aparcamiento cercano para tu comodidad.
                </p>
                
                <div className="bg-black/30 border border-red-900/30 p-6 backdrop-blur-sm mt-8">
                  <h5 className="text-white font-light tracking-wider mb-4">CÓMO LLEGAR</h5>
                  <div className="text-sm space-y-2">
                    <p>• A 5 minutos caminando de la Iglesia del Consuelo</p>
                    <p>• Parking público disponible en Plaza de la Iglesia</p>
                    <p>• Accesible desde la N-332 y AP-7</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;